/**
 * Ability Test Rig — Numpad hotkey bindings.
 *
 * Owns the LocalStorage shape for `Numpad1..Numpad9 -> abilityId` slots.
 * Mutated from AbilityTestRig (UI), read from useGlobalInput (listener).
 * Decoupled so the listener doesn't need to import the panel.
 */

const STORAGE_KEY = 'helix2000.abilityTestRig.hotkeys';

export type HotkeyBindings = Record<string, string>;

export const NUMPAD_SLOT_CODES = [
  'Numpad1', 'Numpad2', 'Numpad3', 'Numpad4', 'Numpad5',
  'Numpad6', 'Numpad7', 'Numpad8', 'Numpad9',
] as const;

export type NumpadSlot = typeof NUMPAD_SLOT_CODES[number];

export function loadHotkeys(): HotkeyBindings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: HotkeyBindings = {};
    for (const k of Object.keys(parsed)) {
      if (typeof parsed[k] === 'string' && parsed[k].length > 0) out[k] = parsed[k];
    }
    return out;
  } catch {
    return {};
  }
}

export function saveHotkeys(bindings: HotkeyBindings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bindings));
  } catch {
    // quota / private mode — silent
  }
}

/** Single-key lookup, used on each keydown — re-reads from localStorage so
 *  changes from the panel are visible without an event bus. */
export function getBoundAbility(code: string): string | null {
  const all = loadHotkeys();
  return all[code] ?? null;
}
